import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { LogOut, RefreshCw, Info, Search, Trash2, Plus, Edit, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { gradientText, gradientButton } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAdmin } from '../hooks/useAdmin';
import { uploadProfilAnnoncePhoto, deleteProfilAnnoncePhoto } from '../lib/supabase';
import ImageCropper from '../components/ImageCropper';
import CountrySelect from '../components/CountrySelect';
import '../styles/phoneInput.css';

// Composant de Carousel pour les photos
const PhotoCarousel = ({ photos }: { photos: string[] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? photos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };
  
  const goToNext = () => {
    const isLastSlide = currentIndex === photos.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };
  
  // Si une seule photo, pas besoin de carousel
  if (photos.length <= 1) {
    return (
      <div className="w-full h-full">
        <img 
          src={photos[0]} 
          alt="Photo" 
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  
  return (
    <div className="relative w-full h-full group">
      <div className="w-full h-full">
        <img 
          src={photos[currentIndex]} 
          alt={`Photo ${currentIndex + 1}`} 
          className="w-full h-full object-cover transition-opacity duration-300"
        />
      </div>
      
      <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
        {photos.map((_, index: number) => (
          <button 
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full ${index === currentIndex ? 'bg-white' : 'bg-white/50'}`}
            aria-label={`Voir photo ${index + 1}`}
          />
        ))}
      </div>
      
      <button 
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToPrevious}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <button 
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-1 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToNext}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
        {currentIndex + 1}/{photos.length}
      </div>
    </div>
  );
};

const AdminAnnonces: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminRole } = useAdmin();
  
  const [annonces, setAnnonces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentAnnonce, setCurrentAnnonce] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string>('');
  
  // État pour la nouvelle annonce
  const [formData, setFormData] = useState({
    nom: '',
    sexe: 'Homme',
    age: 25,
    pays: '',
    ville: '',
    description: '',
    prix: 0,
    orientation_sexuelle: '',
    type_relation: 'court terme' as 'court terme' | 'long terme',
    intentions_court_terme: [] as string[],
    intentions_long_terme: [] as string[],
    contact: '',
    active: true,
    photos_urls: [] as string[]
  });
  
  // État pour l'upload de photo
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photosPreviews, setPhotosPreviews] = useState<string[]>([]);
  
  // Options pour les intentions
  const intentionsCourtTerme = ['Conclure', 'Resto', 'Prendre un verre', 'On verra', 'Autres'];
  const intentionsLongTerme = ['Mariage', 'Enfant', 'Fidélité', 'Libre', 'Polygamie'];
  
  // Ajouter les options d'orientation sexuelle
  const orientationOptions = [
    { value: 'hetero', label: 'Hétéro' },
    { value: 'gay', label: 'Gay' },
    { value: 'lesbienne', label: 'Lesbienne' },
    { value: 'bisexuelle', label: 'Bisexuelle' }
  ];
  
  // Vérifier si l'utilisateur est super admin
  const isSuperAdmin = adminRole?.name === 'super_admin';
  
  // Charger la liste des annonces
  const loadAnnonces = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('profil_annonce')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setAnnonces(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des annonces:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les annonces au chargement de la page
  useEffect(() => {
    loadAnnonces();
  }, []);
  
  // Filtrer les annonces en fonction du terme de recherche
  const filteredAnnonces = searchTerm
    ? annonces.filter(annonce => 
        annonce.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        annonce.ville?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        annonce.pays?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : annonces;
  
  // Gestionnaire pour changer les champs de formulaire
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Nouveau gestionnaire pour la mise en forme des numéros de téléphone et URLs
  const handleContactBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    
    // Vérifier si la valeur est un numéro de téléphone
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
    
    if (phoneRegex.test(value) && !value.startsWith('tel:')) {
      // Supprimer tous les caractères non numériques sauf le + au début
      const cleanedPhone = value.startsWith('+') 
        ? '+' + value.substring(1).replace(/\D/g, '') 
        : value.replace(/\D/g, '');
      
      // Convertir en format de lien téléphonique
      value = `tel:${cleanedPhone}`;
    } else if (value && !value.startsWith('http://') && !value.startsWith('https://') && !value.startsWith('tel:')) {
      // Si c'est un lien web sans protocol, ajouter https://
      value = `https://${value}`;
    }
    
    // Mettre à jour la valeur affichée dans le champ
    e.target.value = value;
    
    // Mettre à jour l'état du formulaire
    setFormData(prev => ({ ...prev, contact: value }));
  };
  
  // Gérer les checkboxes pour les intentions
  const handleIntentionChange = (intention: string, type: 'court' | 'long') => {
    if (type === 'court') {
      setFormData(prev => {
        const current = [...prev.intentions_court_terme];
        if (current.includes(intention)) {
          return { ...prev, intentions_court_terme: current.filter(i => i !== intention) };
        } else {
          return { ...prev, intentions_court_terme: [...current, intention] };
        }
      });
    } else {
      setFormData(prev => {
        const current = [...prev.intentions_long_terme];
        if (current.includes(intention)) {
          return { ...prev, intentions_long_terme: current.filter(i => i !== intention) };
        } else {
          return { ...prev, intentions_long_terme: [...current, intention] };
        }
      });
    }
  };
  
  // Modifier handlePhotoChange pour vérifier le nombre de photos
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (selectedPhotos.length >= 4) {
        alert('Vous ne pouvez pas ajouter plus de 4 photos');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setTempImageUrl(reader.result);
          setShowCropper(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Gérer la validation du crop pour plusieurs photos
  const handleCropComplete = (croppedImageUrl: string) => {
    setShowCropper(false);
    
    // Convertir base64 en File pour l'upload
    fetch(croppedImageUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedPhotos(prev => [...prev, file]);
        setPhotosPreviews(prev => [...prev, croppedImageUrl]);
      });
  };

  // Supprimer une photo spécifique
  const handleRemovePhoto = (index: number) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };
  
  // Modifier handleSubmit pour gérer correctement les photos
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Télécharger toutes les photos
      const photoUrls = [];
      for (const photo of selectedPhotos) {
        // Générer un ID unique pour chaque photo
        const photoId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const uploadedUrl = await uploadProfilAnnoncePhoto(photo, photoId);
        if (uploadedUrl) {
          photoUrls.push(uploadedUrl);
        }
      }
      
      const annonceData = {
        ...formData,
        photos_urls: photoUrls, // Utiliser le bon nom de champ
        updated_at: new Date().toISOString(),
        created_at: isEditing ? undefined : new Date().toISOString(),
      };

      if (isEditing && currentAnnonce) {
        // Mise à jour
        const { error } = await supabase
          .from('profil_annonce')
          .update(annonceData)
          .eq('id', currentAnnonce.id);
          
        if (error) throw error;
      } else {
        // Création
        const { error } = await supabase
          .from('profil_annonce')
          .insert([annonceData])
          .select();
          
        if (error) throw error;
      }
      
      // Recharger les annonces et fermer le modal
      await loadAnnonces();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement de l\'annonce:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };
  
  // Modifier handleEdit pour utiliser le bon champ de photos
  const handleEdit = (annonce: any) => {
    setCurrentAnnonce(annonce);
    setFormData({
      nom: annonce.nom || '',
      sexe: annonce.sexe || 'Homme',
      age: annonce.age || 25,
      pays: annonce.pays || '',
      ville: annonce.ville || '',
      description: annonce.description || '',
      prix: annonce.prix || 0,
      orientation_sexuelle: annonce.orientation_sexuelle || '',
      type_relation: annonce.type_relation || 'court terme',
      intentions_court_terme: annonce.intentions_court_terme || [],
      intentions_long_terme: annonce.intentions_long_terme || [],
      contact: annonce.contact || '',
      active: annonce.active !== undefined ? annonce.active : true,
      photos_urls: annonce.photos_urls || []
    });
    setPhotosPreviews(annonce.photos_urls || []);
    setSelectedPhotos([]); // Réinitialiser les photos sélectionnées
    setIsEditing(true);
    setShowAddModal(true);
  };
  
  // Modifier handleDelete pour gérer correctement la suppression des photos
  const handleDelete = async (id: string, photos: string[]) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) return;
    
    try {
      // Supprimer toutes les photos
      for (const photoUrl of photos) {
        await deleteProfilAnnoncePhoto(photoUrl);
      }
      
      // Supprimer l'annonce
      const { error } = await supabase
        .from('profil_annonce')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Recharger les annonces
      await loadAnnonces();
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'annonce:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };
  
  // Activer/désactiver une annonce
  const toggleActiveState = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('profil_annonce')
        .update({ active: !currentState })
        .eq('id', id);
        
      if (error) throw error;
      
      // Recharger les annonces
      await loadAnnonces();
    } catch (err) {
      console.error('Erreur lors de la modification de l\'état de l\'annonce:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };
  
  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      nom: '',
      sexe: 'Homme',
      age: 25,
      pays: '',
      ville: '',
      description: '',
      prix: 0,
      orientation_sexuelle: '',
      type_relation: 'court terme',
      intentions_court_terme: [],
      intentions_long_terme: [],
      contact: '',
      active: true,
      photos_urls: []
    });
    setSelectedPhotos([]);
    setPhotosPreviews([]);
    setCurrentAnnonce(null);
    setIsEditing(false);
  };
  
  // Si l'utilisateur n'est pas super_admin, afficher un message
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center">
        <div className="max-w-md text-center p-6">
          <h1 className="text-2xl font-bold mb-4 text-white">Accès refusé</h1>
          <p className="text-gray-400 mb-6">Vous devez être super administrateur pour accéder à cette page.</p>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gradient-to-r from-primary-start to-primary-end text-white px-6 py-2 rounded-xl"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black">
      {/* En-tête */}
      <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div>
            <h1 className="text-xl font-bold">
              <span className={gradientText}>Gestion des annonces</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAnnonces}
              disabled={loading}
              className="text-gray-400 hover:text-white"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">{t('admin.dashboard.returnToDashboard')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-4 sm:py-6">
        {/* Barre de recherche et bouton d'ajout */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
          <div className="relative flex-1 max-w-lg">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une annonce..."
              className="w-full bg-gray-900/50 border border-gray-700 rounded-lg p-2 sm:p-3 pl-9 sm:pl-10 text-white text-sm sm:text-base"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          </div>
          
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className={`${gradientButton} text-white py-2 px-4 rounded-lg flex items-center gap-2`}
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une annonce</span>
          </button>
        </div>
        
        {/* Messages d'erreur */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-white flex items-start gap-2">
            <Info className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {/* Liste des annonces */}
        <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl overflow-hidden">
          <div className="p-3 sm:p-5 border-b border-primary-start/20">
            <h3 className="text-base sm:text-lg font-medium text-white">Liste des annonces ({filteredAnnonces.length})</h3>
          </div>
          
          {loading ? (
            <div className="p-6 sm:p-8 flex justify-center">
              <LoadingSpinner size="medium" fullScreen={false} text="Chargement des annonces..." />
            </div>
          ) : filteredAnnonces.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredAnnonces.map(annonce => (
                <div 
                  key={annonce.id} 
                  className={`rounded-xl overflow-hidden ${
                    annonce.active ? 'bg-gray-900/50' : 'bg-gray-900/20 opacity-70'
                  } border border-gray-800 transition-all`}
                >
                  <div className="aspect-square overflow-hidden relative">
                    {annonce.photos_urls && annonce.photos_urls.length > 0 ? (
                      <PhotoCarousel photos={annonce.photos_urls} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        <span className="text-gray-500">Aucune photo</span>
                      </div>
                    )}
                    
                    {/* Badge actif/inactif */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-semibold ${
                      annonce.active 
                        ? 'bg-green-500/80 text-white' 
                        : 'bg-red-500/80 text-white'
                    }`}>
                      {annonce.active ? 'Actif' : 'Inactif'}
                    </div>
                    
                    {/* Type de relation */}
                    <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-semibold bg-primary-end/80 text-white">
                      {annonce.type_relation === 'court terme' ? 'Court terme' : 'Long terme'}
                    </div>
                  </div>
                  
                  <div className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-white">{annonce.nom}</h3>
                      <div className="text-sm text-white">{annonce.age} ans</div>
                    </div>
                    
                    <div className="text-sm text-gray-400 mb-2">
                      {annonce.ville}{annonce.ville && annonce.pays ? ', ' : ''}{annonce.pays}
                    </div>
                    
                    {annonce.prix > 0 && (
                      <div className="text-amber-500 font-semibold mb-2">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(annonce.prix)}
                      </div>
                    )}
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {annonce.type_relation === 'court terme' 
                        ? annonce.intentions_court_terme?.map((intention: string) => (
                            <span key={intention} className="text-xs bg-primary-start/20 text-primary-start px-2 py-0.5 rounded">
                              {intention}
                            </span>
                          ))
                        : annonce.intentions_long_terme?.map((intention: string) => (
                            <span key={intention} className="text-xs bg-primary-end/20 text-primary-end px-2 py-0.5 rounded">
                              {intention}
                            </span>
                          ))
                      }
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800">
                      {/* Actions */}
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleEdit(annonce)}
                          className="text-blue-400 hover:text-blue-300"
                          title="Modifier"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => toggleActiveState(annonce.id, annonce.active)}
                          className={`${annonce.active ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'}`}
                          title={annonce.active ? 'Désactiver' : 'Activer'}
                        >
                          {annonce.active ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => handleDelete(annonce.id, annonce.photos_urls || [])}
                          className="text-red-400 hover:text-red-300"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        {new Date(annonce.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 sm:p-8 text-center text-gray-400">
              Aucune annonce trouvée
            </div>
          )}
        </div>
      </main>
      
      {/* Modal d'ajout/édition */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-gray-900 to-black rounded-2xl max-w-3xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-primary-start/20">
            <div className="p-6 border-b border-primary-start/20 flex justify-between items-center bg-gradient-to-r from-primary-start/10 to-primary-end/10">
              <h3 className={`text-xl font-bold ${gradientText}`}>
                {isEditing ? 'Modifier l\'annonce' : 'Ajouter une annonce'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 custom-scrollbar">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Nom</label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white placeholder-gray-500 focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Sexe</label>
                    <select
                      name="sexe"
                      value={formData.sexe}
                      onChange={handleChange}
                      required
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    >
                      <option value="Homme">Homme</option>
                      <option value="Femme">Femme</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Âge</label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      required
                      min="18"
                      max="100"
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Pays</label>
                    <CountrySelect
                      value={formData.pays}
                      onChange={(value) => setFormData(prev => ({ ...prev, pays: value }))}
                      placeholder="Sélectionnez un pays"
                      className="w-full"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Ville</label>
                    <input
                      type="text"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Orientation sexuelle</label>
                    <select
                      name="orientation_sexuelle"
                      value={formData.orientation_sexuelle}
                      onChange={handleChange}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    >
                      <option value="">Sélectionnez une orientation</option>
                      {orientationOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Prix</label>
                    <input
                      type="number"
                      name="prix"
                      value={formData.prix}
                      onChange={handleChange}
                      min="0"
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Contact</label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      onBlur={handleContactBlur}
                      placeholder="https://... ou numéro de téléphone"
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">Saisissez un lien ou un numéro de téléphone</p>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Type de relation</label>
                    <select
                      name="type_relation"
                      value={formData.type_relation}
                      onChange={handleChange}
                      required
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    >
                      <option value="court terme">Court terme</option>
                      <option value="long terme">Long terme</option>
                    </select>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">État</label>
                    <select
                      name="active"
                      value={formData.active ? "true" : "false"}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === "true" }))}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200"
                    >
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2 space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">
                      Photos ({photosPreviews.length}/4)
                    </label>
                    
                    {/* Affichage des prévisualisations avec carousel */}
                    {photosPreviews.length > 0 && (
                      <div className="w-full aspect-square mb-4 rounded-xl overflow-hidden border-2 border-primary-start/50">
                        <PhotoCarousel photos={photosPreviews} />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {/* Bouton d'ajout de photo */}
                      {photosPreviews.length < 4 && (
                        <div className="relative aspect-square">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                          />
                          <div className={`${gradientButton} rounded-xl w-full h-full flex flex-col items-center justify-center gap-2 transition-all duration-200`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-white text-sm">Ajouter</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Aperçu des photos */}
                      {photosPreviews.map((preview, index) => (
                        <div key={index} className="relative aspect-square">
                          <img 
                            src={preview}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover rounded-xl border-2 border-primary-start/50"
                          />
                          <button
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute top-2 right-2 bg-black/70 p-1.5 rounded-full text-white hover:bg-red-500 transition-colors duration-200"
                            type="button"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          {index === 0 && (
                            <div className="absolute bottom-2 left-2 bg-primary-start/80 px-2 py-1 rounded-lg">
                              <span className="text-white text-xs">Photo principale</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {photosPreviews.length === 0 && (
                      <p className="text-gray-500 text-sm mt-2">Ajoutez jusqu'à 4 photos</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2 space-y-1">
                    <label className="block text-sm font-medium text-primary-start mb-1">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl p-3 text-white focus:border-primary-start focus:ring-1 focus:ring-primary-start transition-all duration-200 resize-none"
                    ></textarea>
                  </div>
                  
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4">
                    <div>
                      <label className="block text-sm font-medium text-primary-start mb-3">
                        Intentions court terme
                        {formData.type_relation !== 'court terme' && (
                          <span className="text-xs text-gray-500 ml-1">(non applicable)</span>
                        )}
                      </label>
                      <div className="space-y-2">
                        {intentionsCourtTerme.map(intention => (
                          <div key={intention} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`court-${intention}`}
                              checked={formData.intentions_court_terme.includes(intention)}
                              onChange={() => handleIntentionChange(intention, 'court')}
                              disabled={formData.type_relation !== 'court terme'}
                              className="w-4 h-4 text-primary-end bg-gray-800 border-gray-700 rounded focus:ring-primary-end"
                            />
                            <label htmlFor={`court-${intention}`} className="ml-2 text-sm text-gray-300">
                              {intention}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-primary-start mb-3">
                        Intentions long terme
                        {formData.type_relation !== 'long terme' && (
                          <span className="text-xs text-gray-500 ml-1">(non applicable)</span>
                        )}
                      </label>
                      <div className="space-y-2">
                        {intentionsLongTerme.map(intention => (
                          <div key={intention} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`long-${intention}`}
                              checked={formData.intentions_long_terme.includes(intention)}
                              onChange={() => handleIntentionChange(intention, 'long')}
                              disabled={formData.type_relation !== 'long terme'}
                              className="w-4 h-4 text-primary-end bg-gray-800 border-gray-700 rounded focus:ring-primary-end"
                            />
                            <label htmlFor={`long-${intention}`} className="ml-2 text-sm text-gray-300">
                              {intention}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="p-6 border-t border-primary-start/20 bg-gradient-to-r from-primary-start/10 to-primary-end/10">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="bg-gray-800 text-white py-2.5 px-5 rounded-xl hover:bg-gray-700 transition-colors duration-200"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className={`${gradientButton} text-white py-2.5 px-5 rounded-xl transition-all duration-200 disabled:opacity-50`}
                  disabled={saving}
                >
                  {saving ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                      Enregistrement...
                    </div>
                  ) : (
                    isEditing ? 'Mettre à jour' : 'Ajouter'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Composant ImageCropper */}
      {showCropper && (
        <ImageCropper
          imageUrl={tempImageUrl}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropper(false);
            setTempImageUrl('');
          }}
          aspectRatio={1}
        />
      )}
    </div>
  );
};

export default AdminAnnonces; 